import { css } from '@emotion/react'
import { ExternalStyles, HFlow, isEmpty, Text, Theme, useTheme, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import {
  RadioOption,
  resolveName,
  SimNaoEnum,
  SimNaoRadioGroupField,
  SimNaoRadioGroupFieldProps,
} from 'components/form'
import { RequiredLabel } from 'components/form/core/RequiredLabel/RequiredLabel'
import { getIn } from 'final-form'
import { useForm } from 'react-final-form'

export interface SimNaoRadioGroupListFieldProps<T extends string> extends Omit<SimNaoRadioGroupFieldProps, 'onChange'> {
  options: RadioOption<T>[]
  description?: string
  labelStyle?: ExternalStyles
  onChange?: (value: Record<T, SimNaoEnum>) => void
}

export function SimNaoRadioGroupListField<T extends string>(props: Readonly<SimNaoRadioGroupListFieldProps<T>>) {
  const { name, label, description, options, required, labelStyle, onChange, ...rest } = props

  const theme = useTheme()
  const { getState } = useForm()
  const styles = createStyles(theme)

  const hasDescription = !isEmpty(description)

  const handleChange = (option: T, value: SimNaoEnum) => {
    if (onChange) {
      const fieldValue = getIn(getState().values, resolveName(name))
      onChange({
        ...fieldValue,
        [option]: value,
      })
    }
  }

  return (
    <VFlow vSpacing={0.5}>
      {hasDescription &&
        (required ? (
          <RequiredLabel fontWeight='bold' label={description} />
        ) : (
          <Text fontWeight='bold'>{description}</Text>
        ))}
      <Box style={styles.box}>
        <VFlow vSpacing={0} style={styles.list}>
          <div css={styles.header}>
            {required && !hasDescription ? (
              <RequiredLabel fontWeight='bold' label={label} style={labelStyle} />
            ) : (
              <Text fontWeight='bold' style={labelStyle}>
                {label}
              </Text>
            )}
          </div>
          {options.map((option) => (
            <HFlow key={option.value} alignItems='center' justifyContent='space-between' style={styles.listItem}>
              <Text>{option.label}</Text>
              <SimNaoRadioGroupField
                name={name[option.value.toString()]}
                hSpacing={0.5}
                style={{ hFlow: styles.radio }}
                onChange={(value) => handleChange(option.value, value.currentTarget.value as SimNaoEnum)}
                {...rest}
              />
            </HFlow>
          ))}
        </VFlow>
      </Box>
    </VFlow>
  )
}

const createStyles = (theme: Theme) => ({
  box: css`
    padding: 0;
  `,
  list: css`
    & > :not(:last-child) {
      border-bottom: 1px solid ${theme.pallete.gray.c80};
    }
  `,
  header: css`
    padding: 0.875rem 1rem;
  `,
  listItem: css`
    padding: 0.75rem 1rem;
  `,
  radio: css`
    justify-content: flex-end;
  `,
})
